// Package imports:
import React from 'react';
import { connect, ConnectedProps } from 'react-redux';
// Style imports:
// Component imports:
import Row from 'react-bootstrap/Row';
import Col from 'react-bootstrap/Col';
import DatePickerRange from '../../common/DatePickerRange';
import StickyHeaderTable from '../../common/StickyHeaderTable';
// Service imports:
import { formatDate, getDatePair } from '../../../services/utils';
// Action imports:
import { requestAlertsSummary } from '../../../redux-store/actions/reportActions';
// Type imports:
import { RootState } from '../../../redux-store/reducers';
import { IStatusCount } from '../../../types/api/ReportTypes';
import { exportToExcel, statusCountToString } from './helperFunctions';
import SortFilterTable from '../../common/SortFilterTable';

//@ts-ignore
window.$.DataTable = require('datatables.net')

// interface IOwnProps {};
type ReduxProps = ConnectedProps<typeof connector>
type Props = ReduxProps;

interface IState {
    tableData: { alertID: string; description: string; timeStamp: string, numAlerts: string; statusCount: IStatusCount; }[];
}

class ReportOfAlertSummary extends React.Component<Props, IState> {
    private dataTableInstance: any;

    constructor(props: Props) {
        super(props);
        this.state = {
            tableData: [],
        };
        this.dataTableInstance = null;
    }

    isLoted = false;
    isError = false;

    componentDidMount() {
        this.initializeDataTable();
        this.fetchData();
    }
    componentDidUpdate(prevProps: Props) {
        if (this.props.fromDate !== prevProps.fromDate || this.props.toDate !== prevProps.toDate) {
            this.fetchData();
        }
        this.updateDataTable();
    }
    componentWillUnmount() {
        if (this.dataTableInstance) {
            this.dataTableInstance?.destroy();
        }
    }
    initializeDataTable = () => {
        // @ts-ignore
        this.dataTableInstance = window.$('#reportTable').DataTable({
            orderCellsTop: true,
            fixedHeader: false,
            pageLength: 100,
            scrollCollapse: true,
            scroller: true,
            deferRender: true,
            paging: false,
            info: false,
            searching: true,
            order: [[0, 'asc']],
            bLengthChange: false,
            dom: 'Bfrtip',
            buttons: ['copy', 'csv', 'excel', 'pdf', 'print']
        });
        $('.dt-button').removeClass('dt-button buttons-html5').addClass('btn-dark btn btn-sm');
    }

    // ===================================== Fetch Data From API =====================================
    fetchData = () => {
        const { fromDate, toDate, accessTokenV2, alertsSummaryMap, requestAlertsSummary } = this.props;
        const datePair = getDatePair(fromDate, toDate);
        const alertsSummaryInfo = alertsSummaryMap[datePair];
        if (!alertsSummaryInfo) {
            requestAlertsSummary(accessTokenV2, fromDate, toDate);
        }
        this.isLoted = true;
    }

    // ===================================== Update Data Table Info =====================================
    updateDataTable = () => {
        const { alertsSummaryMap, fromDate, toDate } = this.props;
        const datePair = getDatePair(fromDate, toDate);
        const alertsSummaryInfo = alertsSummaryMap[datePair];
        // ----------------- If Error loading data -----------------
        if (alertsSummaryInfo instanceof Error) {
            this.isError = true;
        }
        // ----------------- If data is still loading -----------------
        else if (alertsSummaryInfo === 'waiting') {
            this.isLoted = true;
        }
        // ----------------- If data is loaded -----------------
        else if (this.isLoted && Array.isArray(alertsSummaryInfo)) {
            const tableData = alertsSummaryInfo.map((alertSummary) => ({
                alertID: String(alertSummary.id),
                description: alertSummary.description,
                timeStamp: String(alertSummary.latest_alert),
                numAlerts: String(alertSummary.count_alerts),
                statusCount: alertSummary.status_count
            }));
            this.isLoted = false;
            this.setState({ tableData });
        }
    }

    render () {
        const { tableData } = this.state;

        return (
            <>
                <Row className='SecondaryNavBar'>
                    <Col xs={12} className='DatePickerRangeCol'>
                        <DatePickerRange />
                    </Col>
                </Row>
                <Row className='TableTitle'>
                    <Col xs={12} className='TableTitle-col'>
                        <h5 className='SectionTitle'>Alert Summary per Client ID</h5> 
                        <button 
                            id='excelButton' 
                            onClick={exportToExcel} 
                            className='VerySmallButton btn btn-dark btn-sm'
                        >Excel</button>
                    </Col>
                </Row>
                <Row className='TableRow'>
                    <Col xs={12} className='TableCol'>
                        <StickyHeaderTable hideScrollbar={false}>
                            <SortFilterTable
                                data={tableData}
                                isLoading={this.isLoted}
                                isError={this.isError}
                                rowId={({ alertID }) => alertID ?? undefined}
                                columns={[
                                    {
                                        title: 'Alert ID',
                                        textAlign: 'left',
                                        dataAccessor: ({ alertID }) => alertID,
                                        simpleSortable: ({ alertID }) => Number(alertID),
                                        filter: {
                                            id: 'Alert ID',
                                            type: 'string',
                                            stringValue: ({ alertID }) => alertID
                                        }
                                    },
                                    {
                                        title: 'Description',
                                        textAlign: 'left',
                                        dataAccessor: ({ description }) => description,
                                        simpleSortable: ({ description }) => description,
                                    },
                                    {
                                        title: 'Last Alert Timestamp',
                                        textAlign: 'left',
                                        dataAccessor: ({ timeStamp }) => formatDate(timeStamp),
                                        simpleSortable: ({ timeStamp }) => timeStamp,
                                        filter: {
                                            id: 'Last Alert Timestamp',
                                            type: 'string',
                                            stringValue: ({ timeStamp }) => formatDate(timeStamp)
                                        }
                                    },
                                    {
                                        title: 'Number of Alerts',
                                        textAlign: 'right',
                                        dataAccessor: ({ numAlerts }) => numAlerts,
                                        simpleSortable: ({ numAlerts }) => Number(numAlerts),
                                    },
                                    {
                                        title: 'Status',
                                        textAlign: 'left',
                                        dataAccessor: ({ statusCount }) => (
                                            statusCountToString(statusCount)
                                        ),
                                    },
                                ]}
                            />
                        </StickyHeaderTable>
                    </Col>
                </Row>
            </>
        );
    }
}

function mapStateToProps({ dashboardReducer, reportReducer, loginReducer }: RootState) {
    const { dayRange } = dashboardReducer;
    const { fromDate, toDate } = dayRange;
    const { accessTokenV2 } = loginReducer;
    const { alertsSummaryMap } = reportReducer;
    return {
        fromDate,
        toDate,
        accessTokenV2,
        alertsSummaryMap
    };
}

const connector = connect(mapStateToProps, { requestAlertsSummary });
export default connector(ReportOfAlertSummary);


